import type {
  AdminRecurringCancelInput,
  AdminSubscriptionRow,
  MySubscriptionView,
  RecurringSettingsInput,
  CheckoutCatalog,
  CheckoutInput,
  CheckoutStart,
  CheckoutView,
  AdminOrderDetail,
  AdminOrderRow,
  AdminProductRow,
  ChangePriceInput,
  CreateProductInput,
  ManualOrderInput,
  OfferSettings,
  OfferSettingsInput,
  OfferView,
  OrderGrantInput,
  PaymentModeInput,
  OrderLinkInput,
  OrderNoteInput,
  OrderRefundInput,
  OrderStatus,
  PaymentsStatusView,
  ReconcileRow,
  UpdateProductInput,
} from '@creativitas/shared';
import { apiFetch } from '@/lib/api';

/** Оплата: заказы, товары, настройки (docs/PAYMENTS.md, этап 2). */

export const ADMIN_ORDERS_KEY = ['admin', 'orders'] as const;
export const ADMIN_PRODUCTS_KEY = ['admin', 'products'] as const;
export const ADMIN_PAYMENTS_STATUS_KEY = ['admin', 'payments-status'] as const;
export const OFFER_KEY = ['payments', 'offer'] as const;

export const fetchPaymentsStatus = (): Promise<PaymentsStatusView> => apiFetch('/admin/payments/status');

export const savePaymentsMode = (input: PaymentModeInput): Promise<PaymentsStatusView> =>
  apiFetch('/admin/payments/mode', { method: 'PUT', body: input });

export const saveOfferSettings = (input: OfferSettingsInput): Promise<OfferSettings> =>
  apiFetch('/admin/payments/offer', { method: 'PUT', body: input });

export const fetchProducts = (): Promise<{ items: AdminProductRow[] }> => apiFetch('/admin/products');
export const createProduct = (input: CreateProductInput): Promise<{ items: AdminProductRow[] }> =>
  apiFetch('/admin/products', { method: 'POST', body: input });
export const updateProduct = (id: string, input: UpdateProductInput): Promise<{ items: AdminProductRow[] }> =>
  apiFetch(`/admin/products/${id}`, { method: 'PUT', body: input });
export const changeProductPrice = (id: string, input: ChangePriceInput): Promise<{ items: AdminProductRow[] }> =>
  apiFetch(`/admin/products/${id}/price`, { method: 'POST', body: input });

export const fetchOrders = (filter: { status?: OrderStatus; userId?: string } = {}): Promise<{ items: AdminOrderRow[] }> => {
  const params = new URLSearchParams();
  if (filter.status) params.set('status', filter.status);
  if (filter.userId) params.set('userId', filter.userId);
  const query = params.toString();
  return apiFetch(`/admin/orders${query ? `?${query}` : ''}`);
};
export const fetchOrder = (id: string): Promise<AdminOrderDetail> => apiFetch(`/admin/orders/${id}`);
export const grantOrder = (id: string, input: OrderGrantInput): Promise<AdminOrderDetail> =>
  apiFetch(`/admin/orders/${id}/grant`, { method: 'POST', body: input });
export const linkOrder = (id: string, input: OrderLinkInput): Promise<AdminOrderDetail> =>
  apiFetch(`/admin/orders/${id}/link`, { method: 'POST', body: input });
export const refundOrder = (id: string, input: OrderRefundInput): Promise<AdminOrderDetail> =>
  apiFetch(`/admin/orders/${id}/refund`, { method: 'POST', body: input });
export const noteOrder = (id: string, input: OrderNoteInput): Promise<AdminOrderDetail> =>
  apiFetch(`/admin/orders/${id}/note`, { method: 'POST', body: input });
export const verifyOrder = (id: string): Promise<AdminOrderDetail> =>
  apiFetch(`/admin/orders/${id}/verify`, { method: 'POST' });
export const createManualOrder = (input: ManualOrderInput): Promise<AdminOrderDetail> =>
  apiFetch('/admin/orders/manual', { method: 'POST', body: input });
export const reconcileOrders = (invoiceIds: string): Promise<{ items: ReconcileRow[] }> =>
  apiFetch('/admin/orders/reconcile', { method: 'POST', body: { invoiceIds } });

/** Кнопки оформления в кабинете. */
export const fetchOffer = (): Promise<OfferView> => apiFetch('/payments/offer');

/* Прямая оплата (docs/payments/ROBOKASSA_DIRECT.md): витрина, счёт, его состояние. */
export const CHECKOUT_PRODUCTS_KEY = ['payments', 'products'] as const;
export const checkoutKey = (id: string) => ['payments', 'checkout', id] as const;

export const fetchCheckoutProducts = (): Promise<CheckoutCatalog> => apiFetch('/payments/products');
export const startCheckout = (input: CheckoutInput): Promise<CheckoutStart> =>
  apiFetch('/payments/checkout', { method: 'POST', body: input });
export const fetchCheckout = (id: string): Promise<CheckoutView> => apiFetch(`/payments/checkouts/${id}`);

/* Автопродление (docs/payments/RECURRING.md) */
export const MY_SUBSCRIPTIONS_KEY = ['payments', 'subscriptions'] as const;
export const ADMIN_SUBSCRIPTIONS_KEY = ['admin', 'subscriptions'] as const;

export const fetchMySubscriptions = (): Promise<{ items: MySubscriptionView[] }> => apiFetch('/payments/subscriptions');
export const cancelMySubscription = (id: string): Promise<MySubscriptionView> =>
  apiFetch(`/payments/subscriptions/${id}/cancel`, { method: 'POST', body: {} });
export const saveRecurringSetting = (input: RecurringSettingsInput): Promise<PaymentsStatusView> =>
  apiFetch('/admin/payments/recurring', { method: 'PUT', body: input });
export const fetchAdminSubscriptions = (): Promise<{ items: AdminSubscriptionRow[] }> => apiFetch('/admin/payments/subscriptions');
export const adminCancelSubscription = (id: string, input: AdminRecurringCancelInput): Promise<{ items: AdminSubscriptionRow[] }> =>
  apiFetch(`/admin/payments/subscriptions/${id}/cancel`, { method: 'POST', body: input });
